












import {
  Advisor,
  Component,
  ComponentConfig,
  ComponentStyle,
  ComponentStyleDefinition,
  InjectComponent,
  ModelProp,
  SectionType,
  Vue,
  Watch
} from "@zoovu/runner-browser-api";
import {
  AdvisorView,
  AnimationsAndTransitionsConfiguration,
  ColorsConfiguration,
  ComparisonView,
  CustomCSSConfiguration,
  FontsConfiguration,
  QuestionnaireView,
  ResultsSectionView
} from "@zoovu/runner-web-design-base";
import store from "@/store/store";
import { MutationMethod } from "vuex";
import { Getter, Mutation } from "vuex-class";
import CustomTopProductConfiguration from "@/configuration/customized-top-products-settings";
import CustomizedStandardTextsConfiguration from "@/configuration/customized-standard-texts";
import { EnumCustomAttributes } from "@/enums/EnumCustomAttributes";
import VueDOMPurifyHTML from "vue-dompurify-html";
import QuestionTopView from "./question-top.vue";

Vue.use(VueDOMPurifyHTML);

@Component({
  name: "AdvisorView",
  mixins: [AdvisorView],
  components: {
    QuestionTopView
  }
})
export default class AdvisorViewExtended extends Vue {
  @ComponentStyle()
  componentStyle!: ComponentStyleDefinition;

  @ComponentConfig(ColorsConfiguration)
  private colors!: ColorsConfiguration;

  @ComponentConfig(FontsConfiguration)
  private fonts!: FontsConfiguration;

  @ComponentConfig(CustomizedStandardTextsConfiguration)
  private standardTexts!: CustomizedStandardTextsConfiguration;

  @ComponentConfig(CustomCSSConfiguration)
  private customCssConfiguration!: CustomCSSConfiguration;

  @ComponentConfig(CustomTopProductConfiguration)
  private topProductConfiguration!: CustomTopProductConfiguration;

  @ComponentConfig(AnimationsAndTransitionsConfiguration)
  animationsAndTransitionsConfiguration: AnimationsAndTransitionsConfiguration;

  @ModelProp()
  private advisor!: Advisor;

  @InjectComponent("QuestionnaireView")
  questionnaireView!: QuestionnaireView;

  @InjectComponent("ComparisonView")
  comparisonView!: ComparisonView;

  @InjectComponent("ResultsSectionView")
  resultsSectionView!: ResultsSectionView;

  @Mutation
  setAdvisor!: MutationMethod;

  @Mutation
  setNumberOfProducts!: MutationMethod;

  @Mutation
  setShowProductPrice!: MutationMethod;

  @Mutation
  setShowPagination!: MutationMethod;

  @Mutation
  setShowResultCount!: MutationMethod;

  @Mutation
  setShowResultsCountOnQuestionnaire!: MutationMethod;

  @Mutation
  setProductClickoutDisable!: MutationMethod;

  @Getter
  scrollMarginTop!: number;

  beforeCreate() {
    this.$store = store;
  }

  setNumberOfAllProducts({ response }: XMLHttpRequest) {
    if (!response) return;
    const { numberOfProducts } = JSON.parse(response);
    this.setNumberOfProducts(numberOfProducts);
  }

  customAssistantSettings() {
    const { parameters } = this.advisor.flowStepsNavigation.flowSteps[0].questions[0];
    if (parameters.hasOwnProperty(EnumCustomAttributes.ENABLE_PAGINATION))
      this.setShowPagination(parameters.enablePagination);
    if (parameters.hasOwnProperty(EnumCustomAttributes.ENABLE_PRODUCT_PRICE))
      this.setShowProductPrice(parameters.enableProductPrice);
    if (parameters.hasOwnProperty(EnumCustomAttributes.ENABLE_RESULTS_COUNTER))
      this.setShowResultCount(parameters.enableResultsCounter);
    if (parameters.hasOwnProperty(EnumCustomAttributes.ENABLE_RESULTS_COUNTER_ON_QUESTIONNAIRE))
      this.setShowResultsCountOnQuestionnaire(parameters.enableResultsCounterOnQuestionnaire);
    if (parameters.hasOwnProperty(EnumCustomAttributes.DISABLE_PRODUCT_CLICKOUT))
      this.setProductClickoutDisable(parameters.productClickoutDisabled);
  }

  mounted() {
    if (this.scrollMarginTop && this.$root.containerDivId) {
      const wrapper = document.querySelector(`#${this.$root.containerDivId}`);
      if (wrapper) {
        wrapper.style.scrollMarginTop = `${this.scrollMarginTop}px`;
      }
    }
  }

  previousStepIndex!: number;

  get flowStepData() {
    return {
      sectionType: this.advisor.advisorNavigation.currentSection.type,
      stepIndex: this.advisor.flowStepsNavigation.currentStepIndex
    };
  }

  @Watch("flowStepData", { deep: true })
  onFlowStepDataChange(curr, prev) {
    this.previousStepIndex = prev.stepIndex;
    if (this.shouldSkipCurrentQuestion) {
      if (
        this.previousStepIndex < curr.stepIndex ||
        (prev.sectionType === SectionType.QUESTIONNAIRE &&
          curr.sectionType === SectionType.RESULTS_PAGE)
      ) {
        this.advisor.advisorNavigation.next();
      } else if (
        this.previousStepIndex > curr.stepIndex ||
        (prev.sectionType === SectionType.RESULTS_PAGE &&
          curr.sectionType === SectionType.QUESTIONNAIRE)
      ) {
        this.advisor.advisorNavigation.back();
      }
    }
  }

  @Getter
  shouldSkipCurrentQuestion!: () => boolean;

  created() {
    this.customAssistantSettings();
    const { setNumberOfAllProducts } = this;
    const oldXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(
      method: string,
      url: string,
      async: boolean,
      user?: string | null | undefined,
      password?: string | null | undefined
    ) {
      if (url.includes("products")) this.onload = () => setNumberOfAllProducts(this);
      return oldXHROpen.apply(this, [method, url, async, user, password]);
    };
    this.setAdvisor(this.advisor);
  }
}
